document.addEventListener("theme:cart:remove", (event) => {
  const item = event.detail.item;
  sessionStorage.setItem("lastRemovedFromCart", JSON.stringify(item));
});

document.addEventListener("theme:cart:change", async (event) => {
  // Inline comment: event.detail.cart is the latest cart (if retrievable)
  const cart = await getCart();
  const { item, cart_threshold } = window.promotionalItemWithPurchase;
  const lastRemovedFromCart = sessionStorage.getItem("lastRemovedFromCart")
    ? JSON.parse(sessionStorage.getItem("lastRemovedFromCart"))
    : {};
  const isLastRemovedFromCart = lastRemovedFromCart.handle == item.handle;

  if (item == null) return;
  if (isLastRemovedFromCart) return;
  const promotionalItemInCart = findPromotionalItem(cart.items, item.first_available_variant_id);
  const cartTotalPrice =
    promotionalItemInCart != null ? cart.total_price - promotionalItemInCart.final_price : cart.total_price;
  const isAboveThreshold = cartTotalPrice >= cart_threshold * 100;

  if (isAboveThreshold && promotionalItemInCart == null) {
    await updateCart(item.first_available_variant_id, 0);
    await addToCart([
      {
        id: item.first_available_variant_id,
        quantity: 1,
        properties: { _promotional_item: true },
      },
    ]);
    document.dispatchEvent(new CustomEvent("theme:cart:build"));
  }

  if (!isAboveThreshold && promotionalItemInCart != null) {
    await updateCart(promotionalItemInCart.key, 0);
    document.dispatchEvent(new CustomEvent("theme:cart:build"));
  }
});

/**
 * Find a promotional item in the cart.
 * Returns the matching item object if found, otherwise null.
 *
 * @param {Array} items - The cart's items array (from /cart.js)
 * @param {number|string} variantId - The variant ID to match
 * @returns {Object|null} The matching item or null
 *
 * @example
 * const promoItem = findPromotionalItem(cart.items, 50546845581641);
 * if (promoItem) {
 *   console.log("Found promo item:", promoItem);
 * } else {
 *   console.log("No promo item found");
 * }
 */
function findPromotionalItem(items, variantId) {
  if (!Array.isArray(items) || !variantId) return null;

  return (
    items.find((item) => {
      const idMatch = String(item.variant_id) === String(variantId) || String(item.id) === String(variantId);

      const props = item.properties || {};

      return idMatch;
    }) || null
  );
}

/**
 * Fetch the latest Shopify cart JSON directly from /cart.js
 * and return it as a parsed object.
 *
 * @returns {Promise<Object>} Resolves to the full cart object
 * @example
 * const cart = await getCart();
 * console.log(cart.item_count, cart.items);
 */
async function getCart() {
  try {
    const response = await fetch("/cart.js", {
      method: "GET",
      credentials: "same-origin", // Inline comment: ensures it includes cookies/session
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache", // Inline comment: ensures fresh cart, not cached
      },
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    const cart = await response.json();
    return cart;
  } catch (error) {
    console.error("[getCart] Error fetching cart:", error);
    return null;
  }
}

/**
 * Update a cart line item by variant ID and quantity.
 * Sends a POST request to /cart/update.js.
 *
 * @param {number|string} variantId - The variant ID to update.
 * @param {number} quantity - The new quantity (0 to remove item).
 * @returns {Promise<Object|null>} Resolves with the updated cart object or null on error.
 *
 * @example
 * // Set variant 123456789 to quantity 2
 * const cart = await updateCart(123456789, 2);
 * console.log(cart.item_count);
 */
async function updateCart(variantId, quantity) {
  try {
    const body = {
      updates: {
        [variantId]: quantity,
      },
    };

    const response = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Cart update failed: ${response.status}`);
    }

    const cart = await response.json();
    return cart;
  } catch (error) {
    console.error("[updateCart] Error:", error);
    return null;
  }
}

/**
 * Add one or more items to the Shopify cart.
 * Expects an array of item objects shaped for /cart/add.js:
 *   [{ id: 123456789, quantity: 2, properties: { key: 'value' } }, ...]
 *
 * @param {Array} items - Array of item objects
 * @returns {Promise<Object|null>} Resolves with the response JSON or null on error
 *
 * @example
 * await addToCart([
 *   { id: 50546845581641, quantity: 1 },
 *   { id: 50841022333257, quantity: 2, properties: { _promotional_item: true } }
 * ]);
 */
async function addToCart(items) {
  if (!Array.isArray(items) || items.length === 0) {
    console.error("[addToCart] 'items' must be a non-empty array");
    return null;
  }

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Add to cart failed (${response.status}): ${text}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("[addToCart] Error:", error);
    return null;
  }
}
